import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { IBuildingProfile } from '../frontend-libs/evlapp-types';

import { t } from '../common/i18n';
import { useAppDispatch, useDocumentTitle } from '../common/hooks';
import routes from '../common/constants/routes';
import {
  Button,
  Card,
  Content,
  FormMessage,
  Header,
  Icon,
  TextField,
  PhoneField,
  Modal,
  Text,
  TipMessage,
} from '../common/components';
import { formatDateTime } from '../common/utils/Date/Date';

import { addMissingContact } from '../Poll/services/PollService';
import {
  ProfileEmailCheckStatus,
  checkProfileContactEmail,
  getProfile,
  sendAppInvitation,
} from './services/UnitService';
import {
  IVotingProxyRecord,
  createVotingProxy,
  downloadRepresentationFile,
  endVotingProxy,
  getVotingProxy,
} from './services/RepresentationService';
import { RepresentativeForm } from './components/RepresentativeForm';
import Yup, { backendViolations, YupEmail, YupPhone } from '../common/utils/FormValidation';
import { setFlashMessage } from '../common/store/uiSlice';
import { useModal } from '../common/components/Modal/useModal';

type ProfileDetailTab = 'overview' | 'proxy';

export const ProfileDetail: React.FC = () => {
  const namespace = 'Units.ProfileDetail';

  const dispatch = useAppDispatch();
  /* const security = useAppSelector(selectSecurity).ui;
  const hasSubscription = security.tokens?.filter((item) => item.type === 'building-subscription').length || 0;
  const shouldShowModal = !hasSubscription; */

  useDocumentTitle(t('title', namespace));

  const { id = null } = useParams();
  const [profile, setProfile] = useState<IBuildingProfile>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isInvitationLoading, setIsInvitationLoading] = useState(false);

  const [isShown, toggle] = useModal();
  const canBeInvited = profile.email || profile.phone;

  const [activeTab, setActiveTab] = useState<ProfileDetailTab>('overview');
  const [votingProxy, setVotingProxy] = useState<IVotingProxyRecord | null>(null);
  const [isEditingProxy, setIsEditingProxy] = useState(false);

  // Předběžná kontrola e-mailu — spouští se po dopsání (debounce) nebo při opuštění pole
  const [emailCheck, setEmailCheck] = useState<{ email: string; status: ProfileEmailCheckStatus } | null>(null);
  const emailCheckTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (emailCheckTimer.current) window.clearTimeout(emailCheckTimer.current);
    },
    []
  );

  const runEmailCheck = (email: string) => {
    const value = email.trim();
    // API voláme až pro smysluplně vypadající e-mail; u registrovaného vlastníka je pole zamčené
    if (!id || profile.isUser || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailCheck(null);
      return;
    }

    checkProfileContactEmail(id, value)
      .then((res) => setEmailCheck({ email: value, status: res.status }))
      .catch(() => setEmailCheck(null));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    setEmailCheck(null);
    if (emailCheckTimer.current) window.clearTimeout(emailCheckTimer.current);
    const value = e.target.value;
    emailCheckTimer.current = window.setTimeout(() => runEmailCheck(value), 600);
  };

  const handleEmailBlur = () => {
    if (emailCheckTimer.current) window.clearTimeout(emailCheckTimer.current);
    runEmailCheck(formik.values.email || '');
  };

  const profileState: 'registered' | 'invited' | 'new' = profile.isUser
    ? 'registered'
    : profile.invitedAt
      ? 'invited'
      : 'new';

  useEffect(() => {
    async function fetchProfile() {
      try {
        const data = await getProfile(id);
        setProfile(data);
        setIsLoading(false);
      } catch {
        setIsEmpty(true);
        setIsLoading(false);
      }
    }
    fetchProfile();

    if (id !== null) {
      getVotingProxy(id)
        .then((data) => setVotingProxy(data.active))
        .catch(() => setVotingProxy(null));
    }
  }, [id]);

  const handleSendAppInvitation = () => {
    if (!profile.id) return;

    setIsInvitationLoading(true);

    sendAppInvitation(profile.id)
      .then(() => {
        toggle();
        setIsInvitationLoading(false);
        // pozvánka se odesílá asynchronně — stav přepneme optimisticky, bez čekání na refetch
        // (formát shodný s BE `toDateTimeString`, ať jde protáhnout přes formatDateTime)
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const invitedAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:00`;
        setProfile((prev) => ({ ...prev, invitedAt }));
        dispatch(setFlashMessage({ message: t('InvitationModal.Flash.success', namespace), type: 'success' }));
      })
      .catch(() => {
        setIsInvitationLoading(false);
        dispatch(setFlashMessage({ message: t('InvitationModal.Flash.error', namespace), type: 'error' }));
      });
  };

  const formik = useFormik<IBuildingProfile>({
    enableReinitialize: true,
    initialValues: {
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      company: profile.company || '',
      phone: profile.phone || '',
      email: profile.email || '',
      birthDate: profile.birthDate,
      birthNumber: profile.birthNumber || '',
    },

    // BE u kontaktů vyžaduje obojí (bez telefonu vrací 422) — FE validace musí odpovídat
    validationSchema: Yup.object({
      email: YupEmail,
      phone: YupPhone,
    }),

    onSubmit: (values) => {
      if (!profile.id) return;

      // Předběžná kontrola už našla obsazený e-mail a hláška je zobrazená — submit by skončil 422
      if (
        emailCheck &&
        emailCheck.email === (values.email || '').trim() &&
        (emailCheck.status === 'taken_member' || emailCheck.status === 'taken_other')
      ) {
        formik.setSubmitting(false);
        return;
      }

      addMissingContact(profile.id, { email: values.email || '', phone: values.phone || '' })
        .then((res) => {
          setProfile(res.profile);
          dispatch(setFlashMessage({ message: t('Form.Flash.success', namespace), type: 'success' }));
        })
        .catch((error) => {
          const validations = backendViolations(formik, error);

          if (validations === 'valid') {
            dispatch(setFlashMessage({ message: t('Form.Flash.error', namespace), type: 'error' }));
          }
        })
        .finally(() => {
          formik.setSubmitting(false);
        });
    },
  });

  // Status vlastníka se opakuje v obou tabech (uvnitř karet) — taby tak drží stejnou pozici jako v detailu jednotky
  const statusMessage = (
    <FormMessage
      type={profileState === 'registered' ? 'success' : profileState === 'invited' ? 'warning' : 'info'}
      variant="dark"
      align="left"
      className="mb-3"
    >
      <Text as="div" size="body-medium" weight="600">
        {profileState === 'invited' && profile.invitedAt
          ? t('State.invited.title', namespace, { date: formatDateTime(profile.invitedAt) })
          : t(`State.${profileState}.title`, namespace)}
      </Text>
      <Text as="div" size="body-small" color="Neutral700" weight="400" className="mt-1">
        {t(`State.${profileState}.text`, namespace)}
        {profileState === 'registered' && (
          <>
            {' '}
            <a href="mailto:pomoc@evlastnici.cz">{t('State.registered.link', namespace)}</a>.
          </>
        )}
      </Text>
    </FormMessage>
  );

  return (
    <>
      <Header title={`${t('Header.title', namespace)}`} customBacklink={routes.units} />
      <Content isLoading={isLoading} isEmpty={isEmpty}>
        <div className="UnitTabs">
          <button
            type="button"
            className={`UnitTabs__item ${activeTab === 'overview' ? 'UnitTabs__item--active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <Icon name="profile" color="Inherit" />
            {t('Tabs.overview', namespace)}
          </button>
          <button
            type="button"
            className={`UnitTabs__item ${activeTab === 'proxy' ? 'UnitTabs__item--active' : ''}`}
            onClick={() => setActiveTab('proxy')}
          >
            <Icon name="handshake" color="Inherit" />
            {t('Tabs.proxy', namespace)}
          </button>
        </div>

        {activeTab === 'overview' && (
          <Card className="Card--content">
            {statusMessage}
            <form onSubmit={formik.handleSubmit}>
              {profile.firstName && (
                <TextField
                  name="firstName"
                  label={t('Form.Fields.FirstName.label', namespace)}
                  placeholder={t('Form.Fields.FirstName.placeholder', namespace)}
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  className="mb-2"
                  disabled={true}
                />
              )}
              {profile.lastName && (
                <TextField
                  name="lastName"
                  label={t('Form.Fields.LastName.label', namespace)}
                  placeholder={t('Form.Fields.LastName.placeholder', namespace)}
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  className="mb-2"
                  disabled={true}
                />
              )}
              {profile.company && (
                <TextField
                  name="company"
                  label={t('Form.Fields.Company.label', namespace)}
                  placeholder={t('Form.Fields.Company.placeholder', namespace)}
                  value={formik.values.company}
                  onChange={formik.handleChange}
                  className="mb-2"
                  disabled={true}
                />
              )}
              <TextField
                name="email"
                type="email"
                label={t('Form.Fields.Email.label', namespace)}
                placeholder={t('Form.Fields.Email.placeholder', namespace)}
                value={formik.values.email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                error={{ touched: formik.touched.email, message: formik.errors.email }}
                className="mb-2"
                disabled={profile.isUser}
              />
              {emailCheck &&
                emailCheck.email === (formik.values.email || '').trim() &&
                (emailCheck.status === 'taken_member' || emailCheck.status === 'taken_other') && (
                  <TipMessage className="mb-2">
                    {t('EmailCheck.taken', namespace)}{' '}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab('proxy');
                      }}
                    >
                      {t('EmailCheck.takenLink', namespace)}
                    </a>
                    .
                  </TipMessage>
                )}
              <PhoneField
                name="phone"
                label={t('Form.Fields.Phone.label', namespace)}
                placeholder={t('Form.Fields.Phone.placeholder', namespace)}
                value={formik.values.phone}
                onChange={(phone) => formik.setFieldValue('phone', phone)}
                error={{ message: formik.errors.phone, touched: formik.touched.phone }}
                className="mb-2"
                disabled={profile.isUser}
              />

              <TextField
                name="birthNumber"
                label={t('Form.Fields.BirthNumber.label', namespace)}
                placeholder={t('Form.Fields.BirthNumber.placeholder', namespace)}
                value={formik.values.birthNumber}
                onChange={formik.handleChange}
                className="mb-2"
                disabled={true}
              />

              {!profile.isUser && (
                <div className="Flex FlexAlign--center">
                  <Button
                    onClick={formik.handleSubmit}
                    loading={formik.isSubmitting}
                    disabledDeps={['demo', 'expired']}
                    title={t('Form.Fields.Submit.title', namespace)}
                    className="mr-1"
                  />
                  {canBeInvited && (
                    <>
                      <Button
                        title={t(
                          profileState === 'invited' ? 'InvitationModal.linkTitleResend' : 'InvitationModal.linkTitle',
                          namespace
                        )}
                        onClick={toggle}
                        variant="text"
                        disabledDeps={['demo', 'expired']}
                      />
                    </>
                  )}
                </div>
              )}

              <Modal isShown={isShown} onHide={toggle}>
                <Text size="h2" align="center" className="mb-2">
                  {t('InvitationModal.title', namespace)}
                </Text>
                <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
                  {t('InvitationModal.subtitle', namespace)}
                </Text>
                <div className="Flex FlexColumn FlexAlign--center">
                  <Button
                    title={t('InvitationModal.confirm', namespace)}
                    wide={true}
                    onClick={handleSendAppInvitation}
                    loading={isInvitationLoading}
                  />
                </div>
              </Modal>
            </form>
          </Card>
        )}

        {activeTab === 'proxy' && (
          <Card className="Card--content">
            {statusMessage}
            <Text size="h3" color="Neutral800" className="mb-1">
              {t('VotingProxy.title', namespace)}
            </Text>
            <Text as="div" size="body-small" color="Neutral600" className="mb-2">
              {t('VotingProxy.description', namespace)}
            </Text>

            {votingProxy ? (
              <>
                <Text as="div" size="body-medium" weight="600" color="Neutral800">
                  {t('VotingProxy.current', namespace, { name: votingProxy.representativeName })}
                  {votingProxy.validTo && ` (${t('VotingProxy.validTo', namespace, { date: votingProxy.validTo })})`}
                </Text>
                {votingProxy.files.map((file) => (
                  <Button
                    key={file.id}
                    variant="text"
                    size="small"
                    title={file.name}
                    onClick={() => downloadRepresentationFile(file)}
                  />
                ))}
                <div>
                  <Button
                    variant="text"
                    size="small"
                    title={t('VotingProxy.end', namespace)}
                    onClick={() => {
                      endVotingProxy(votingProxy.id).then(() => {
                        setVotingProxy(null);
                        dispatch(setFlashMessage({ message: t('VotingProxy.ended', namespace), type: 'success' }));
                      });
                    }}
                  />
                </div>
              </>
            ) : isEditingProxy ? (
              <RepresentativeForm
                submitLabel={t('VotingProxy.submit', namespace)}
                excludeProfileId={profile.id}
                onSubmit={async (values) => {
                  if (!profile.id) return;
                  const record = await createVotingProxy(profile.id, values);
                  setVotingProxy(record);
                  setIsEditingProxy(false);
                  dispatch(setFlashMessage({ message: t('VotingProxy.success', namespace), type: 'success' }));
                }}
              />
            ) : (
              <div>
                <Button
                  size="small"
                  title={t('VotingProxy.add', namespace)}
                  disabledDeps={['demo', 'expired']}
                  onClick={() => setIsEditingProxy(true)}
                />
              </div>
            )}
          </Card>
        )}

        {/* <div className="Registry">
          <Text size="h2" color="Neutral800" className="mt-3 mb-3">
            Historie změn
          </Text>

          <div className="timeline-container">

            {id && (
              <>
                <div className={shouldShowModal ? 'blur-content timeline-locked' : ''}>
                  <BuildingLogsTimeline
                    filters={{ profiles: [id] }}
                  />
                </div>
                {shouldShowModal && (
                  <SubscriptionModal />
                )}
              </>
            )}

          </div>
        </div> */}
      </Content>
    </>
  );
};
